import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { Sentry } from '../lib/sentry';
import { ensureFreshToken, getTimeUntilExpiry } from '../api/client';
import { getAuthApiUrl, getDataApiUrl, getActiveEnv, setActiveEnv } from '../config/env';
import { applyTheme, themeFromConfig, DEFAULT_THEME } from '../constants/colors';


export interface User {
  id: string;
  phoneNumber: string | null;
  email: string | null;
  phoneVerified: boolean;
  emailVerified: boolean;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  bio: string | null;
  isSeller: boolean;
  sellerWhitelisted: boolean;
  sellerInterestSubmittedAt: string | null;
  themeConfig: string | null;
  needsProfileSetup: boolean;
  useBalanceForPurchases?: boolean;
  hasAddress: boolean | null;
  hasPaymentMethod: boolean | null;
}

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  user: User;
  // Operator-controlled default data plane for this account (PRODUCTION|SANDBOX).
  defaultEnv?: string;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  register: (phoneNumber: string, password: string, email: string) => Promise<void>;
  logout: () => void;
  enrollSeller: () => Promise<void>;
  submitSellerInterest: (note: string) => Promise<void>;
  refreshAccessToken: () => Promise<string | null>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ACCESS_TOKEN_KEY = 'auction_access_token';
const REFRESH_TOKEN_KEY = 'auction_refresh_token';
const USER_KEY = 'auction_user';

// Minimum time before expiry to trigger a proactive refresh (2 minutes)
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

/**
 * Consume OAuth tokens from the URL fragment produced by the backend
 * redirect callback: `#accessToken=...&refreshToken=...&expiresIn=...`.
 *
 * If present, persists tokens, fetches the user profile, and strips the
 * fragment. Returns the hydrated auth state or null if no fragment found.
 */
async function consumeOAuthFragment(): Promise<{ accessToken: string; user: User } | null> {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;
  const params = new URLSearchParams(hash.slice(1));
  const oauthError = params.get('oauthError');
  const accessToken = params.get('accessToken');
  const refreshToken = params.get('refreshToken');

  const strip = () => {
    try {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch { /* ignore */ }
  };

  if (oauthError) {
    console.warn('OAuth redirect error:', oauthError);
    strip();
    return null;
  }
  if (!accessToken || !refreshToken) return null;

  // Persist tokens first so fetchers using them read a consistent state.
  sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

  try {
    const resp = await fetch(`${getDataApiUrl()}/api/v1/users/me`, {
      headers: { 'Authorization': `Bearer ${accessToken}` },
    });
    if (!resp.ok) throw new Error(`Failed to fetch user profile: ${resp.status}`);
    const user: User = await resp.json();
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    strip();
    return { accessToken, user };
  } catch (e) {
    console.error('Failed to hydrate user after OAuth redirect', e);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    strip();
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem(USER_KEY);
    if (stored) {
      const u = JSON.parse(stored);
      Sentry.setUser({ id: u.id, username: u.username, ip_address: '{{auto}}' });
      return u;
    }
    return null;
  });
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  });
  const [isLoading, setIsLoading] = useState(true);

  // Ref to track scheduled refresh timeout
  const refreshTimeoutRef = useRef<number | null>(null);

  const saveAuth = useCallback((response: AuthResponse) => {
    setAccessToken(response.accessToken);
    setUser(response.user);
    sessionStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));
    Sentry.setUser({ id: response.user.id, username: response.user.username, ip_address: '{{auto}}' });
    // Adopt the operator-controlled default environment on login (the App Info toggle overrides until
    // the next login). Skipped in local dev.
    if (response.defaultEnv && getActiveEnv() !== 'local') {
      setActiveEnv(response.defaultEnv.toUpperCase() === 'SANDBOX' ? 'sandbox' : 'production');
    }
  }, []);

  const clearAuth = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    Sentry.setUser(null);

    // Clear any scheduled refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const newToken = await ensureFreshToken();

    if (newToken) {
      setAccessToken(newToken);
      // Update user from localStorage (it was updated by the API client)
      const storedUser = localStorage.getItem(USER_KEY);
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } else {
      clearAuth();
    }

    return newToken;
  }, [clearAuth]);

  // Schedule a proactive token refresh before it expires
  const scheduleTokenRefresh = useCallback(() => {
    // Clear any existing scheduled refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    const timeUntilExpiry = getTimeUntilExpiry();
    if (timeUntilExpiry === null) return;

    // Schedule refresh 2 minutes before expiry, or immediately if less than 2 minutes left
    const refreshIn = Math.max(0, timeUntilExpiry - REFRESH_BUFFER_MS);

    console.debug(`Token expires in ${Math.round(timeUntilExpiry / 1000)}s, scheduling refresh in ${Math.round(refreshIn / 1000)}s`);

    refreshTimeoutRef.current = window.setTimeout(async () => {
      console.debug('Proactively refreshing token...');
      const newToken = await refreshAccessToken();
      if (newToken) {
        // Schedule next refresh
        scheduleTokenRefresh();
      }
    }, refreshIn);
  }, [refreshAccessToken]);

  // Initialize auth state and handle visibility changes
  useEffect(() => {
    const init = async () => {
      // Consume OAuth tokens delivered via URL fragment from the server-side
      // redirect flow (#accessToken=...&refreshToken=...&expiresIn=...).
      const consumed = await consumeOAuthFragment();
      if (consumed) {
        setAccessToken(consumed.accessToken);
        setUser(consumed.user);
        Sentry.setUser({ id: consumed.user.id, username: consumed.user.username, ip_address: '{{auto}}' });
        scheduleTokenRefresh();
        setIsLoading(false);
        return;
      }

      const storedUser = localStorage.getItem(USER_KEY);
      const storedAccessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
      const refreshTokenVal = localStorage.getItem(REFRESH_TOKEN_KEY);

      // If we have user data but no access token (e.g., new tab), try to refresh
      if (storedUser && !storedAccessToken && refreshTokenVal) {
        await refreshAccessToken();
      }

      // Schedule proactive refresh if we have a token
      if (sessionStorage.getItem(ACCESS_TOKEN_KEY)) {
        scheduleTokenRefresh();
      }

      setIsLoading(false);
    };

    init();

    // Handle visibility change - refresh token when user returns to tab
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const storedUser = localStorage.getItem(USER_KEY);
        const refreshTokenVal = localStorage.getItem(REFRESH_TOKEN_KEY);

        if (storedUser && refreshTokenVal) {
          console.debug('Tab became visible, ensuring fresh token...');
          const newToken = await ensureFreshToken();
          if (newToken) {
            setAccessToken(newToken);
            scheduleTokenRefresh();
          } else {
            clearAuth();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [refreshAccessToken, scheduleTokenRefresh, clearAuth]);

  const login = useCallback(async (identifier: string, password: string) => {
    const response = await fetch(`${getAuthApiUrl()}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Login failed' }));
      throw new Error(error.message || 'Login failed');
    }

    const data: AuthResponse = await response.json();
    saveAuth(data);
    scheduleTokenRefresh();
  }, [saveAuth, scheduleTokenRefresh]);

  const register = useCallback(async (
    phoneNumber: string,
    password: string,
    email: string
  ) => {
    const response = await fetch(`${getAuthApiUrl()}/api/v1/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phoneNumber, password, email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Registration failed' }));
      throw new Error(error.message || 'Registration failed');
    }

    const data: AuthResponse = await response.json();
    saveAuth(data);
    scheduleTokenRefresh();
  }, [saveAuth, scheduleTokenRefresh]);

  const logout = useCallback(async () => {
    const refreshTokenVal = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshTokenVal && accessToken) {
      try {
        await fetch(`${getAuthApiUrl()}/api/v1/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ refreshToken: refreshTokenVal }),
        });
      } catch (error) {
        console.error('Logout request failed:', error);
      }
    }
    clearAuth();
  }, [accessToken, clearAuth]);

  const enrollSeller = useCallback(async () => {
    // Ensure we have a fresh token first
    const token = await ensureFreshToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${getDataApiUrl()}/api/v1/users/me/sell/enroll`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to enroll as seller' }));
      throw new Error(error.message || 'Failed to enroll as seller');
    }

    const updatedUser: User = await response.json();
    setUser(updatedUser);
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));

    // Refresh the access token to get updated roles (SELLER)
    await refreshAccessToken();
  }, [refreshAccessToken]);

  const submitSellerInterest = useCallback(async (note: string) => {
    const token = await ensureFreshToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${getDataApiUrl()}/api/v1/users/me/sell/interest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ note }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to submit interest' }));
      throw new Error(error.message || 'Failed to submit interest');
    }

    const updatedUser: User = await response.json();
    setUser(updatedUser);
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
  }, []);

  const refreshUser = useCallback(async () => {
    // Ensure we have a fresh token first
    const token = await ensureFreshToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${getDataApiUrl()}/api/v1/users/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user data');
    }

    const updatedUser: User = await response.json();
    setUser(updatedUser);
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
  }, []);

  // Apply theme when user changes (login, logout, profile update)
  useEffect(() => {
    if (user?.themeConfig) {
      applyTheme(themeFromConfig(user.themeConfig));
    } else {
      applyTheme(DEFAULT_THEME);
    }
  }, [user?.themeConfig]);

  const value: AuthContextType = {
    user,
    accessToken,
    isAuthenticated: !!user && (!!accessToken || !!localStorage.getItem(REFRESH_TOKEN_KEY)),
    isLoading,
    login,
    register,
    logout,
    enrollSeller,
    submitSellerInterest,
    refreshAccessToken,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
